import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Mic, 
  MicOff, 
  Play, 
  Pause, 
  Square, 
  Clock, 
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowLeft,
  FileText,
  StopCircle
} from 'lucide-react';
import Header from './Header';

// Using relative URLs
const API = '/api';

class RealtimeAudioChat {
  constructor() {
    this.peerConnection = null;
    this.conversationStarted = false; // Flag to prevent duplicate AI triggers
    this.dataChannel = null;
    this.audioElement = null;
    this.selectedFrameworkNames = null; // Store the framework names for greeting
  }

  async init(apiPrefix, sessionId = null) {
    try {
      // Get session from backend
      const requestBody = sessionId ? JSON.stringify({ session_id: sessionId }) : JSON.stringify({});
      
      const tokenResponse = await fetch(`${apiPrefix}/realtime/session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('token')}`
        },
        body: requestBody
      });
      const data = await tokenResponse.json();
      if (!data.client_secret?.value) {
        throw new Error("Failed to get session token");
      }
      
      // Store system prompt, framework, and voice settings from backend
      this.systemPrompt = data.system_prompt;
      this.framework = data.framework;
      this.voice = data.voice || "echo";  // Voice from super admin settings
      this.model = data.model || "gpt-4o-mini-realtime-preview-2024-12-17";
      
      console.log(`🎯 Session configured for framework: ${this.framework}`);
      console.log(`🎙️ Using voice: ${this.voice}, model: ${this.model}`);

      // Create and set up WebRTC peer connection
      this.peerConnection = new RTCPeerConnection();
      this.setupAudioElement();
      await this.setupLocalAudio();
      this.setupDataChannel();

      // Create and send offer
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      // Send offer to backend and get answer
      const response = await fetch(`${apiPrefix}/realtime/negotiate`, {
        method: "POST",
        body: offer.sdp,
        headers: {
          "Content-Type": "application/sdp",
          "Authorization": `Bearer ${localStorage.getItem('token')}`
        }
      });

      const { sdp: answerSdp } = await response.json();
      const answer = {
        type: "answer",
        sdp: answerSdp
      };

      await this.peerConnection.setRemoteDescription(answer);
      console.log("WebRTC connection established");
      return true;
    } catch (error) {
      console.error("Failed to initialize audio chat:", error);
      throw error;
    }
  }

  setupAudioElement() {
    this.audioElement = document.createElement("audio");
    this.audioElement.autoplay = true;
    document.body.appendChild(this.audioElement);

    this.peerConnection.ontrack = (event) => {
      this.audioElement.srcObject = event.streams[0];
    };
  }

  async setupLocalAudio() {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach(track => {
      this.peerConnection.addTrack(track, stream);
    });
  }

  setupDataChannel() {
    this.dataChannel = this.peerConnection.createDataChannel("oai-events");
    
    this.dataChannel.onopen = () => {
      console.log("✅ Data channel opened");
      
      // CRITICAL: Check if conversation already started (prevents data channel reconnection triggers)
      if (this.conversationStarted) {
        console.warn("⚠️ Data channel reopened but conversation already started, ignoring");
        return;
      }
      
      // Send system prompt via session.update (ONCE, when channel opens)
      if (this.systemPrompt) {
        console.log("📤 Sending system instructions to Nova");
        console.log("📋 System Prompt Preview:", this.systemPrompt.substring(0, 200) + "...");
        
        this.sendEvent({
          type: "session.update",
          session: {
            modalities: ["audio", "text"],
            instructions: this.systemPrompt,
            voice: this.voice,  // Use voice from super admin settings (not hardcoded)
            input_audio_format: "pcm16",
            output_audio_format: "pcm16",
            input_audio_transcription: {
              model: "whisper-1"
            },
            turn_detection: {
              type: "server_vad",
              threshold: 0.9,              // MAXIMUM sensitivity - virtually only loud clear speech
              prefix_padding_ms: 800,      // Even more audio context
              silence_duration_ms: 2000    // 2 full seconds of silence required
            },
            temperature: 0.8,
            max_response_output_tokens: 4096
          }
        });
        console.log("✅ System instructions configured with transcription enabled");
        
        // Mark conversation as started BEFORE triggering
        this.conversationStarted = true;
        
        // Trigger AI to start immediately (no setTimeout delay to avoid race conditions)
        console.log("🎤 Triggering Nova to start conversation (ONCE)...");
        console.log("🎯 Framework for assessment:", this.selectedFrameworkNames || "unknown");
        
        // Simple trigger - let the system prompt handle everything
        this.sendEvent({
          type: "response.create"
        });
      }
    };
    
    this.dataChannel.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // Log all events for debugging
        console.log("📡 Realtime Event:", data.type, data);
        
        // Update AI status based on events
        if (data.type === "response.audio.delta") {
          // AI is speaking - audio chunk received
          if (this.onStatusUpdate) {
            this.onStatusUpdate('speaking');
          }
        } else if (data.type === "response.audio.done") {
          // AI finished speaking - explicit done event
          if (this.onStatusUpdate) {
            this.onStatusUpdate('listening');
          }
        } else if (data.type === "input_audio_buffer.speech_started") {
          // User started speaking
          if (this.onStatusUpdate) {
            this.onStatusUpdate('user_speaking');
          }
        } else if (data.type === "input_audio_buffer.speech_stopped") {
          // User stopped speaking
          if (this.onStatusUpdate) {
            this.onStatusUpdate('processing');
          }
        } else if (data.type === "response.created") {
          // AI is generating response
          if (this.onStatusUpdate) {
            this.onStatusUpdate('thinking');
          }
        } else if (data.type === "response.done") {
          // Full response completed (includes audio, text, etc.)
          if (this.onStatusUpdate) {
            this.onStatusUpdate('listening');
          }
        }
        
        // Capture transcript from multiple event types
        if (data.type === "conversation.item.created" && data.item) {
          console.log("📝 Conversation item created:", data.item);
          
          if (data.item.role === "user" || data.item.role === "assistant") {
            // Extract text content
            let content = '';
            
            // Try multiple content extraction methods
            if (data.item.content && Array.isArray(data.item.content)) {
              // Method 1: Look for text or transcript in content array
              for (const contentItem of data.item.content) {
                if (contentItem.type === 'text' && contentItem.text) {
                  content += contentItem.text;
                } else if (contentItem.type === 'input_text' && contentItem.text) {
                  content += contentItem.text;
                } else if (contentItem.type === 'audio' && contentItem.transcript) {
                  content += contentItem.transcript;
                } else if (contentItem.transcript) {
                  content += contentItem.transcript;
                } else if (contentItem.text) {
                  content += contentItem.text;
                }
              }
            } else if (data.item.formatted?.text) {
              // Method 2: Formatted text
              content = data.item.formatted.text;
            } else if (data.item.formatted?.transcript) {
              // Method 3: Formatted transcript
              content = data.item.formatted.transcript;
            }
            
            if (content && this.onTranscriptUpdate) {
              console.log("✅ Extracted content:", content);
              this.onTranscriptUpdate({
                ...data.item,
                extractedContent: content
              });
            }
          }
        }
        
        // Also capture text from conversation.item.input_audio_transcription.completed
        if (data.type === "conversation.item.input_audio_transcription.completed") {
          console.log("🎤 Transcription completed:", data);
          
          // Validate transcript to filter out background noise
          if (data.transcript && this.onUserTranscriptComplete) {
            const transcript = data.transcript.trim();
            
            // AGGRESSIVE HALLUCINATION FILTERING WITH COMPOUND SCORING
            
            // Filter out very short inputs (likely noise)
            if (transcript.length < 5) {
              console.warn("⚠️ Ignoring very short transcript (likely noise):", transcript);
              return;
            }
            
            // COMPOUND HALLUCINATION DETECTION
            // Score based on multiple suspicious patterns - if score >= 2, it's likely a hallucination
            const hallucinationPatterns = [
              { pattern: /boyfriend|girlfriend|romantic|dating|love|relationship/i, score: 3, name: "personal_life" },
              { pattern: /sun|sunny|weather|outside|outdoors/i, score: 2, name: "weather" },
              { pattern: /relax|treat yourself|enjoy|have fun|good time/i, score: 2, name: "leisure" },
              { pattern: /thank you for watching|subscribe|like and share|hit the bell/i, score: 5, name: "youtube" },
              { pattern: /transcript|caption|subtitle|closed caption/i, score: 5, name: "media_meta" },
              { pattern: /music playing|applause|laughter|\[music\]|\[applause\]/i, score: 5, name: "audio_description" },
              { pattern: /gone|left|departed|away|missing/i, score: 1, name: "absence" },
              { pattern: /work to do|get started|let's begin|shall we/i, score: 1, name: "generic_start" },
              { pattern: /neat|cool|nice|interesting|wonderful/i, score: 1, name: "casual_adjectives" },
              { pattern: /sat out|sitting|stood up|walking|moving/i, score: 1, name: "physical_actions" },
            ];
            
            let hallucinationScore = 0;
            let matchedPatterns = [];
            
            for (const {pattern, score, name} of hallucinationPatterns) {
              if (pattern.test(transcript)) {
                hallucinationScore += score;
                matchedPatterns.push(name);
              }
            }
            
            // If score >= 2 or transcript is unusually long without compliance keywords, block it
            if (hallucinationScore >= 2) {
              console.warn(`⚠️ BLOCKED WHISPER HALLUCINATION (score: ${hallucinationScore}, patterns: ${matchedPatterns.join(', ')}):`, transcript);
              return;
            }
            
            // Block transcripts longer than 100 chars that don't mention compliance/security terms
            if (transcript.length > 100) {
              const complianceKeywords = /compliance|security|policy|policies|access|data|system|control|audit|process|document|gdpr|hipaa|soc|iso|encryption|authentication|monitoring|incident|backup|vendor/i;
              if (!complianceKeywords.test(transcript)) {
                console.warn("⚠️ BLOCKED: Long transcript with no compliance keywords (likely hallucination):", transcript);
                return;
              }
            }
            
            // Filter out common noise patterns
            const noisePatterns = [
              /^(bye\.?|hi\.?|yeah\.?|yes\.?|no\.?|ok\.?|okay\.?|thank you\.?|thanks\.?|well\.?)$/i,
              /^[^\w\s]+$/,  // Only punctuation
              /^[\d\s]+$/,   // Only numbers
              /[^\x00-\x7F]/,  // Non-ASCII (Korean, Chinese, etc.)
            ];
            
            const isNoise = noisePatterns.some(pattern => pattern.test(transcript));
            if (isNoise) {
              console.warn("⚠️ Ignoring suspected background noise:", transcript);
              return;
            }
            
            // Log valid transcript
            console.log("✅ Valid user transcript (score: 0):", transcript);
            
            // Only process valid transcripts
            this.onUserTranscriptComplete(data);
          }
        }
        
        // Capture from response.audio_transcript events
        if (data.type === "response.audio_transcript.delta" && data.delta) {
          console.log("🔊 Audio transcript delta:", data.delta);
          if (this.onAudioTranscriptDelta) {
            this.onAudioTranscriptDelta(data);
          }
        }
        
        if (data.type === "response.audio_transcript.done") {
          console.log("🔊 Audio transcript done:", data);
          if (this.onAudioTranscriptComplete) {
            this.onAudioTranscriptComplete(data);
          }
        }
        
        // Capture from response.done which has full output
        if (data.type === "response.done" && data.response) {
          console.log("✅ Response done:", data.response);
          if (data.response.output && Array.isArray(data.response.output)) {
            for (const outputItem of data.response.output) {
              if (outputItem.type === 'message' && outputItem.content) {
                for (const contentItem of outputItem.content) {
                  if (contentItem.type === 'text' && contentItem.text) {
                    console.log("📝 Found text in response.done:", contentItem.text);
                    if (this.onResponseText) {
                      this.onResponseText({
                        role: 'assistant',
                        content: contentItem.text
                      });
                    }
                  }
                }
              }
            }
          }
        }
      } catch (e) {
        console.error("Error parsing data channel message:", e);
      }
    };
  }
  
  sendEvent(event) {
    console.log(`📤 Attempting to send event: ${event.type}`, event);
    
    if (!this.dataChannel) {
      console.error("❌ sendEvent FAILED: Data channel does not exist");
      return false;
    }
    
    if (this.dataChannel.readyState !== "open") {
      console.error(`❌ sendEvent FAILED: Data channel state is "${this.dataChannel.readyState}", not "open"`);
      return false;
    }
    
    try {
      const eventStr = JSON.stringify(event);
      this.dataChannel.send(eventStr);
      console.log(`✅ Event sent successfully: ${event.type} (${eventStr.length} bytes)`);
      return true;
    } catch (error) {
      console.error(`❌ sendEvent FAILED with exception:`, error);
      return false;
    }
  }

  stop() {
    if (this.peerConnection) {
      this.peerConnection.close();
    }
    if (this.audioElement && this.audioElement.srcObject) {
      this.audioElement.srcObject.getTracks().forEach(track => track.stop());
      this.audioElement.remove();
    }
  }
}

const VoiceAssessment = () => {
  const { user, currentWorkspace } = useAuth();
  const navigate = useNavigate();
  const [selectedFramework, setSelectedFramework] = useState(''); // Changed to single framework
  const [promptVersion, setPromptVersion] = useState(null); // Track prompt version
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [sessionId, setSessionId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [transcript, setTranscript] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasOpenAIKey, setHasOpenAIKey] = useState(false);
  const [aiStatus, setAiStatus] = useState('idle'); // idle, listening, speaking, thinking, processing, user_speaking
  const [activeTab, setActiveTab] = useState('conversation'); // conversation, transcript
  const [currentAssistantText, setCurrentAssistantText] = useState('');
  const [isStarting, setIsStarting] = useState(false); // Track if session is being started
  const [startingStatus, setStartingStatus] = useState(''); // Status message during startup
  
  // NEW: Progressive Question Generation State
  const [currentStep, setCurrentStep] = useState(1); // 1: Framework Selection, 2: Question Generation, 3: Voice Assessment
  const [assessmentPlan, setAssessmentPlan] = useState(null);
  const [generationProgress, setGenerationProgress] = useState({
    isGenerating: false,
    currentCategory: null,
    currentCategoryIndex: 0,
    totalCategories: 0,
    completedCategories: [],
    failedCategories: [],
    totalQuestionsGenerated: 0
  });
  const [generatedQuestionnaire, setGeneratedQuestionnaire] = useState(null);
  
  const audioChat = useRef(null);
  const timerRef = useRef(null);
  const transcriptEndRef = useRef(null);
  const isStartingRef = useRef(false); // Prevent duplicate starts

  const frameworks = [
    { id: 'SOC2_TYPE1', name: 'SOC 2 Type 1', color: 'bg-blue-100 text-blue-800 border-blue-300' },
    { id: 'SOC2_TYPE2', name: 'SOC 2 Type 2', color: 'bg-blue-100 text-blue-800 border-blue-300' },
    { id: 'GDPR', name: 'GDPR', color: 'bg-green-100 text-green-800 border-green-300' },
    { id: 'HIPAA', name: 'HIPAA', color: 'bg-purple-100 text-purple-800 border-purple-300' },
    { id: 'ISO27001', name: 'ISO 27001', color: 'bg-orange-100 text-orange-800 border-orange-300' },
    { id: 'PCI_DSS', name: 'PCI DSS', color: 'bg-red-100 text-red-800 border-red-300' }
  ];

  const languages = [
    // Major World Languages
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Spanish (Español)', flag: '🇪🇸' },
    { code: 'fr', name: 'French (Français)', flag: '🇫🇷' },
    { code: 'de', name: 'German (Deutsch)', flag: '🇩🇪' },
    { code: 'it', name: 'Italian (Italiano)', flag: '🇮🇹' },
    { code: 'pt', name: 'Portuguese (Português)', flag: '🇵🇹' },
    { code: 'ru', name: 'Russian (Русский)', flag: '🇷🇺' },
    { code: 'zh', name: 'Chinese - Mandarin (中文)', flag: '🇨🇳' },
    { code: 'ja', name: 'Japanese (日本語)', flag: '🇯🇵' },
    { code: 'ko', name: 'Korean (한국어)', flag: '🇰🇷' },
    { code: 'ar', name: 'Arabic (العربية)', flag: '🇸🇦' },
    { code: 'hi', name: 'Hindi (हिन्दी)', flag: '🇮🇳' },
    
    // European Languages
    { code: 'nl', name: 'Dutch (Nederlands)', flag: '🇳🇱' },
    { code: 'pl', name: 'Polish (Polski)', flag: '🇵🇱' },
    { code: 'sv', name: 'Swedish (Svenska)', flag: '🇸🇪' },
    { code: 'da', name: 'Danish (Dansk)', flag: '🇩🇰' },
    { code: 'no', name: 'Norwegian (Norsk)', flag: '🇳🇴' },
    { code: 'fi', name: 'Finnish (Suomi)', flag: '🇫🇮' },
    { code: 'cs', name: 'Czech (Čeština)', flag: '🇨🇿' },
    { code: 'sk', name: 'Slovak (Slovenčina)', flag: '🇸🇰' },
    { code: 'hu', name: 'Hungarian (Magyar)', flag: '🇭🇺' },
    { code: 'ro', name: 'Romanian (Română)', flag: '🇷🇴' },
    { code: 'bg', name: 'Bulgarian (Български)', flag: '🇧🇬' },
    { code: 'el', name: 'Greek (Ελληνικά)', flag: '🇬🇷' },
    { code: 'tr', name: 'Turkish (Türkçe)', flag: '🇹🇷' },
    { code: 'uk', name: 'Ukrainian (Українська)', flag: '🇺🇦' },
    
    // Asian Languages
    { code: 'th', name: 'Thai (ไทย)', flag: '🇹🇭' },
    { code: 'vi', name: 'Vietnamese (Tiếng Việt)', flag: '🇻🇳' },
    { code: 'id', name: 'Indonesian (Bahasa Indonesia)', flag: '🇮🇩' },
    { code: 'ms', name: 'Malay (Bahasa Melayu)', flag: '🇲🇾' },
    { code: 'tl', name: 'Tagalog (Filipino)', flag: '🇵🇭' },
    { code: 'bn', name: 'Bengali (বাংলা)', flag: '🇧🇩' },
    { code: 'ta', name: 'Tamil (தமிழ்)', flag: '🇮🇳' },
    { code: 'te', name: 'Telugu (తెలుగు)', flag: '🇮🇳' },
    { code: 'mr', name: 'Marathi (मराठी)', flag: '🇮🇳' },
    { code: 'ur', name: 'Urdu (اردو)', flag: '🇵🇰' },
    { code: 'fa', name: 'Persian (فارسی)', flag: '🇮🇷' },
    
    // African & Middle Eastern
    { code: 'he', name: 'Hebrew (עברית)', flag: '🇮🇱' },
    { code: 'sw', name: 'Swahili (Kiswahili)', flag: '🇰🇪' },
    { code: 'af', name: 'Afrikaans', flag: '🇿🇦' },
    { code: 'am', name: 'Amharic (አማርኛ)', flag: '🇪🇹' },
    
    // Americas
    { code: 'pt-br', name: 'Portuguese - Brazil (Português BR)', flag: '🇧🇷' },
    { code: 'es-mx', name: 'Spanish - Mexico (Español MX)', flag: '🇲🇽' },
    
    // Other European
    { code: 'hr', name: 'Croatian (Hrvatski)', flag: '🇭🇷' },
    { code: 'sr', name: 'Serbian (Српски)', flag: '🇷🇸' },
    { code: 'sl', name: 'Slovenian (Slovenščina)', flag: '🇸🇮' },
    { code: 'lt', name: 'Lithuanian (Lietuvių)', flag: '🇱🇹' },
    { code: 'lv', name: 'Latvian (Latviešu)', flag: '🇱🇻' },
    { code: 'et', name: 'Estonian (Eesti)', flag: '🇪🇪' },
    { code: 'is', name: 'Icelandic (Íslenska)', flag: '🇮🇸' }
  ];

  const MAX_DURATION = 60 * 60; // 60 minutes in seconds

  useEffect(() => {
    checkOpenAIConfig();
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioChat.current) {
        audioChat.current.stop();
      }
    };
  }, []);

  // Auto-scroll transcript to bottom
  useEffect(() => {
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [transcript]);

  const getStatusInfo = () => {
    switch (aiStatus) {
      case 'listening':
        return { text: 'Listening...', color: 'text-green-600', icon: 'animate-pulse' };
      case 'speaking':
        return { text: 'Speaking...', color: 'text-blue-600', icon: 'animate-pulse' };
      case 'thinking':
        return { text: 'Thinking...', color: 'text-purple-600', icon: 'animate-spin' };
      case 'processing':
        return { text: 'Processing...', color: 'text-amber-600', icon: 'animate-spin' };
      case 'user_speaking':
        return { text: 'You are speaking...', color: 'text-teal-600', icon: 'animate-pulse' };
      default:
        return { text: 'Ready', color: 'text-slate-600', icon: '' };
    }
  };

  const checkOpenAIConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/openai/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setHasOpenAIKey(response.data.has_key || response.data.configured);
    } catch (error) {
      console.error('Error checking OpenAI config:', error);
      setHasOpenAIKey(false);
    } finally {
      setLoading(false);
    }
  };

  const selectFramework = async (frameworkId) => {
    setSelectedFramework(frameworkId);
    
    // Fetch prompt version for the selected framework
    try {
      const response = await axios.get(`${API}/admin/voice-prompts/${frameworkId}/version`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data && response.data.version) {
        setPromptVersion(response.data.version);
        console.log(`✅ Loaded prompt version ${response.data.version} for ${frameworkId}`);
      }
    } catch (error) {
      console.error('Error fetching prompt version:', error);
      // Set a default version if fetch fails
      setPromptVersion(null);
    }
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => {
        const newTime = prev + 1;
        if (newTime >= MAX_DURATION) {
          handleEndAssessment();
          return MAX_DURATION;
        }
        return newTime;
      });
    }, 1000);
  };

  const pauseTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getRemainingTime = () => {
    return MAX_DURATION - elapsedTime;
  };

  // ============================================================
  // Progressive Question Generation Functions
  // ============================================================

  const proceedToQuestionGeneration = async () => {
    if (!selectedFramework) {
      toast.error('Please select a framework first');
      return;
    }

    if (!hasOpenAIKey) {
      toast.error('OpenAI API key not configured. Please contact administrator.');
      return;
    }

    try {
      setGenerationProgress(prev => ({ ...prev, isGenerating: true }));
      
      // Get the assessment plan from the backend
      const response = await axios.post(`${API}/voice/assessment-plan`, {
        frameworks: [selectedFramework],
        user_id: user.id,
        workspace_id: currentWorkspace?.id
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to create assessment plan');
      }

      setAssessmentPlan(response.data);
      setGenerationProgress(prev => ({
        ...prev,
        totalCategories: response.data.categories.length,
        isGenerating: false
      }));
      
      // Move to step 2
      setCurrentStep(2);
      toast.success(`Assessment plan created with ${response.data.total_categories} categories`);
      
    } catch (error) {
      console.error('Failed to get assessment plan:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to create assessment plan');
      setGenerationProgress(prev => ({ ...prev, isGenerating: false }));
    }
  };

  const startQuestionGeneration = async () => {
    if (!assessmentPlan || !assessmentPlan.categories) {
      toast.error('No assessment plan available');
      return;
    }

    const categories = assessmentPlan.categories;
    const questionsPerCategory = assessmentPlan.questions_per_category || 3;
    
    setGenerationProgress(prev => ({
      ...prev,
      isGenerating: true,
      currentCategoryIndex: 0,
      completedCategories: [],
      failedCategories: [],
      totalQuestionsGenerated: 0
    }));

    const updatedCategories = [...categories];
    let totalQuestions = 0;
    const existingQuestions = [];

    // Generate questions for each category sequentially
    for (let i = 0; i < categories.length; i++) {
      const category = categories[i];
      
      setGenerationProgress(prev => ({
        ...prev,
        currentCategory: category.name,
        currentCategoryIndex: i
      }));

      try {
        const response = await axios.post(`${API}/voice/generate-questions-for-category`, {
          session_id: sessionId,
          category_name: category.name,
          framework: category.framework,
          question_count: questionsPerCategory,
          organization: currentWorkspace?.name || 'the organization',
          existing_questions: existingQuestions.slice(-15) // Last 15 questions for context
        }, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });

        if (response.data.success) {
          const questions = response.data.questions || [];
          updatedCategories[i] = {
            ...category,
            status: 'completed',
            questions: questions
          };
          
          // Add question texts to existing questions for deduplication
          questions.forEach(q => {
            if (q.main_question) {
              existingQuestions.push(q.main_question);
            }
          });
          
          totalQuestions += questions.length;
          
          setGenerationProgress(prev => ({
            ...prev,
            completedCategories: [...prev.completedCategories, category.name],
            totalQuestionsGenerated: totalQuestions
          }));
        } else {
          updatedCategories[i] = {
            ...category,
            status: 'failed',
            error: response.data.error
          };
          
          setGenerationProgress(prev => ({
            ...prev,
            failedCategories: [...prev.failedCategories, category.name]
          }));
        }
      } catch (error) {
        console.error(`Failed to generate questions for ${category.name}:`, error);
        updatedCategories[i] = {
          ...category,
          status: 'failed',
          error: error.message
        };
        
        setGenerationProgress(prev => ({
          ...prev,
          failedCategories: [...prev.failedCategories, category.name]
        }));
      }
    }

    // Update the plan with generated questions
    setAssessmentPlan(prev => ({
      ...prev,
      categories: updatedCategories
    }));

    setGeneratedQuestionnaire({
      categories: updatedCategories,
      total_questions: totalQuestions,
      prompts_used: assessmentPlan.prompts_used
    });

    setGenerationProgress(prev => ({
      ...prev,
      isGenerating: false,
      currentCategory: null
    }));

    const successCount = updatedCategories.filter(c => c.status === 'completed').length;
    if (successCount === categories.length) {
      toast.success(`All ${totalQuestions} questions generated successfully!`);
    } else {
      toast.warning(`Generated questions for ${successCount}/${categories.length} categories`);
    }
  };

  const proceedToVoiceAssessment = () => {
    if (!generatedQuestionnaire || generatedQuestionnaire.total_questions === 0) {
      toast.error('Please generate questions first');
      return;
    }
    setCurrentStep(3);
  };

  const goBackToStep = (step) => {
    if (step < currentStep) {
      setCurrentStep(step);
      if (step === 1) {
        // Reset generation state when going back to framework selection
        setAssessmentPlan(null);
        setGeneratedQuestionnaire(null);
        setGenerationProgress({
          isGenerating: false,
          currentCategory: null,
          currentCategoryIndex: 0,
          totalCategories: 0,
          completedCategories: [],
          failedCategories: [],
          totalQuestionsGenerated: 0
        });
      }
    }
  };

  // ============================================================
  // Original Functions (Updated)
  // ============================================================

  const startAssessment = async () => {
    // CRITICAL: Prevent duplicate session creation
    if (isStartingRef.current || isStarting) {
      console.warn("⚠️ Session already starting, ignoring duplicate call");

      return;
    }

    // Framework selection is mandatory
    if (!selectedFramework) {
      toast.error('Please select a framework');
      return;
    }

    if (!hasOpenAIKey) {
      toast.error('OpenAI API key not configured. Please contact administrator.');
      return;
    }

    // Check if we're in a secure context
    const isSecureContext = window.isSecureContext;

    if (!isSecureContext) {
      toast.error(
        'WebRTC requires a secure context (HTTPS). This feature will work in production deployment.',
        { duration: 8000 }
      );
      return;
    }

    // Set starting flags to prevent double-click
    isStartingRef.current = true;
    setIsStarting(true);
    setStartingStatus('Initializing session...');

    try {
      // Create session in backend with selected framework (single framework only)
      const sessionResponse = await axios.post(`${API}/voice/session/create`, {
        user_id: user.id,
        workspace_id: currentWorkspace?.id,
        framework: selectedFramework, // Single framework now
        language: selectedLanguage
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const newSessionId = sessionResponse.data.session_id;
      setSessionId(newSessionId);
      setStartingStatus('Session created. Setting up audio...');

      // Clean up any existing audio chat instance first
      if (audioChat.current) {
        console.log("🧹 Cleaning up existing audio chat instance");
        audioChat.current.stop();
        audioChat.current = null;
      }

      // Initialize OpenAI Realtime Audio Chat with session_id for language support
      const selectedLang = languages.find(l => l.code === selectedLanguage);
      console.log(`Initializing OpenAI Realtime Audio Chat in ${selectedLang?.name}`);
      setStartingStatus('Connecting to AI voice system...');
      
      audioChat.current = new RealtimeAudioChat();
      
      // Pass the selected framework name for the greeting
      const framework = frameworks.find(f => f.id === selectedFramework);
      const frameworkName = framework ? framework.name : selectedFramework;
      audioChat.current.selectedFrameworkNames = frameworkName;
      console.log("📋 Selected framework for assessment:", frameworkName);
      
      // Set up callbacks for transcript and status updates
      audioChat.current.onTranscriptUpdate = (item) => {
        console.log("🎯 onTranscriptUpdate called with item:", item);
        
        const content = item.extractedContent || item.content?.[0]?.text || item.content?.[0]?.transcript || '';
        
        if (!content) {
          console.warn("⚠️ No content extracted from item:", item);
          return;
        }
        
        const newTurn = {
          role: item.role,
          content: content,
          timestamp: new Date().toISOString()
        };
        
        console.log("💬 Adding to transcript:", newTurn);
        setTranscript(prev => [...prev, newTurn]);
        
        // Also send to backend for storage
        if (newSessionId) {
          axios.post(`${API}/voice/session/conversation-turn`, {
            session_id: newSessionId,
            ...newTurn
          }, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }).catch(err => console.error('Failed to store conversation turn:', err));
        }
      };
      
      // Handle user transcription completion
      audioChat.current.onUserTranscriptComplete = (data) => {
        console.log("🎤 User transcript complete:", data);
        if (data.transcript) {
          const newTurn = {
            role: 'user',
            content: data.transcript,
            timestamp: new Date().toISOString()
          };
          setTranscript(prev => [...prev, newTurn]);
          
          // Send to backend
          if (newSessionId) {
            axios.post(`${API}/voice/session/conversation-turn`, {
              session_id: newSessionId,
              ...newTurn
            }, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            }).catch(err => console.error('Failed to store conversation turn:', err));
          }
        }
      };
      
      // Handle assistant audio transcript
      let currentAssistantTranscript = '';
      audioChat.current.onAudioTranscriptDelta = (data) => {
        currentAssistantTranscript += data.delta;
      };
      
      audioChat.current.onAudioTranscriptComplete = (data) => {
        console.log("🔊 Assistant transcript complete:", currentAssistantTranscript);
        if (currentAssistantTranscript) {
          const newTurn = {
            role: 'assistant',
            content: currentAssistantTranscript,
            timestamp: new Date().toISOString()
          };
          setTranscript(prev => [...prev, newTurn]);
          
          // Send to backend
          if (newSessionId) {
            axios.post(`${API}/voice/session/conversation-turn`, {
              session_id: newSessionId,
              ...newTurn
            }, {
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            }).catch(err => console.error('Failed to store conversation turn:', err));
          }
          
          // Reset
          currentAssistantTranscript = '';
        }
      };
      
      audioChat.current.onStatusUpdate = (status) => {
        setAiStatus(status);
      };
      
      // Handle response text from response.done
      audioChat.current.onResponseText = (data) => {
        console.log("📄 Response text received:", data);
        const newTurn = {
          role: data.role,
          content: data.content,
          timestamp: new Date().toISOString()
        };
        
        // Check if this content is already in transcript (avoid duplicates)
        setTranscript(prev => {
          const exists = prev.some(t => 
            t.role === newTurn.role && 
            t.content === newTurn.content &&
            Math.abs(new Date(t.timestamp).getTime() - new Date(newTurn.timestamp).getTime()) < 5000
          );
          
          if (!exists) {
            console.log("✅ Adding response text to transcript");
            // Send to backend
            if (newSessionId) {
              axios.post(`${API}/voice/session/conversation-turn`, {
                session_id: newSessionId,
                ...newTurn
              }, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
              }).catch(err => console.error('Failed to store conversation turn:', err));
            }
            return [...prev, newTurn];
          }
          console.log("⏭️ Skipping duplicate transcript entry");
          return prev;
        });
      };
      
      setStartingStatus('Establishing secure connection...');
      await audioChat.current.init(`${API}/v1`, newSessionId);
      
      setStartingStatus('Starting assessment...');
      setIsConnected(true);
      setIsListening(true);
      setAiStatus('listening');
      setIsStarting(false); // Clear loading state
      setStartingStatus('');
      startTimer();
      toast.success(`Assessment started in ${selectedLang?.name}!`);

    } catch (error) {
      console.error('Failed to start assessment:', error);
      
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to start assessment';
      toast.error(errorMsg);
      
      setIsConnected(false);
      setIsListening(false);
      setIsStarting(false); // Clear loading state
      setStartingStatus('');
      isStartingRef.current = false; // Reset ref
    } finally {
      // Always reset the ref flag
      isStartingRef.current = false;
    }
  };

  const handlePauseResume = async () => {
    if (isPaused) {
      // Resume
      startTimer();
      setIsPaused(false);
      toast.info('Assessment resumed');
      
      try {
        await axios.post(`${API}/voice/session/${sessionId}/resume`, {}, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      } catch (error) {
        console.error('Failed to resume session:', error);
      }
    } else {
      // Pause
      pauseTimer();
      setIsPaused(true);
      toast.info('Assessment paused');
      
      try {
        await axios.post(`${API}/voice/session/${sessionId}/pause`, {}, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        await axios.post(`${API}/voice/session/${sessionId}/update-duration`, {
          duration_seconds: elapsedTime
        }, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      } catch (error) {
        console.error('Failed to pause session:', error);
      }
    }
  };

  const handleEndAssessment = async () => {
    pauseTimer();
    
    if (audioChat.current) {
      audioChat.current.stop();
    }

    // Save data in background without updating component state
    const saveData = async () => {
      try {
        if (sessionId) {
          // Update duration first
          console.log(`📊 Saving assessment duration: ${elapsedTime} seconds`);
          await axios.post(`${API}/voice/session/${sessionId}/update-duration`, {
            duration_seconds: elapsedTime
          }, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          
          // Store transcript
          const framework = frameworks.find(f => f.id === selectedFramework);
          const frameworkName = framework ? framework.name : selectedFramework;
          const transcriptText = transcript.length > 0 
            ? transcript.map(t => `${t.role}: ${t.content}`).join('\n\n')
            : `Voice assessment completed for ${frameworkName} in ${elapsedTime} seconds.`;
          
          await axios.post(`${API}/voice/session/transcript`, {
            session_id: sessionId,
            transcript: transcriptText,
            messages: transcript.length > 0 ? transcript : [
              {
                role: 'assistant',
                content: `Assessment conducted for framework: ${frameworkName}`,
                timestamp: new Date().toISOString()
              }
            ]
          }, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          
          // Mark session as completed
          console.log('📋 Marking session as completed...');
          const completeResponse = await axios.post(`${API}/voice/session/${sessionId}/complete`, {}, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          console.log('✅ Session marked as completed:', completeResponse.data);
          
          console.log('✅ Assessment data saved and marked as completed');
        }
      } catch (error) {
        console.error('Failed to save assessment data:', error);
      }
    };
    
    // Show loading toast while saving
    toast.loading('Saving assessment...');
    
    // Wait for data to save (including completion status)
    await saveData();
    
    // Navigate after data is saved
    toast.dismiss();
    toast.success('Assessment completed! Loading report...');
    navigate(`/assessment/report/${sessionId}`, { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg overflow-x-hidden">
        <Header />
        <div className="flex items-center justify-center min-h-[80vh]">
          <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg overflow-x-hidden">
      <Header />
      
      {/* Loading Modal */}
      {isStarting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-96">
            <CardContent className="p-6">
              <div className="flex flex-col items-center gap-4">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                  <Mic className="w-6 h-6 text-blue-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    Starting Assessment
                  </h3>
                  <p className="text-sm text-slate-600">
                    {startingStatus || 'Please wait...'}
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    This may take a few seconds
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Content */}
        {!isConnected ? (
          // Multi-Step Setup Screen
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Mic className="w-6 h-6 text-navy-600" />
                AI Voice Assessment
              </CardTitle>
              <CardDescription>
                Comprehensive 45-minute AI-powered compliance assessment with dynamically generated questions.
              </CardDescription>
              
              {/* Step Indicator */}
              <div className="py-8">
                <div className="flex items-center justify-center gap-2">
                  {[1, 2, 3].map((step) => (
                    <div key={step} className="flex items-center">
                      <button
                        onClick={() => step < currentStep && goBackToStep(step)}
                        disabled={step >= currentStep}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                          step === currentStep
                            ? 'bg-navy-600 text-white'
                            : step < currentStep
                            ? 'bg-green-500 text-white cursor-pointer hover:bg-green-600'
                            : 'bg-slate-200 text-slate-500'
                        }`}
                      >
                        {step < currentStep ? <CheckCircle className="w-4 h-4" /> : step}
                      </button>
                      {step < 3 && (
                        <div className={`w-12 h-1 mx-1 rounded ${
                          step < currentStep ? 'bg-green-500' : 'bg-slate-200'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-center gap-8 mt-2 text-xs text-slate-600">
                  <span className={currentStep === 1 ? 'font-semibold text-navy-600' : ''}>Framework</span>
                  <span className={currentStep === 2 ? 'font-semibold text-navy-600' : ''}>Questions</span>
                  <span className={currentStep === 3 ? 'font-semibold text-navy-600' : ''}>Assessment</span>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* STEP 1: Framework & Language Selection */}
              {currentStep === 1 && (
                <>
                  {/* Framework Selection */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">
                      Select Compliance Framework
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                      {frameworks.map((framework) => (
                        <button
                          key={framework.id}
                          onClick={() => selectFramework(framework.id)}
                          data-testid={`framework-${framework.id}`}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            selectedFramework === framework.id
                              ? `${framework.color} border-current shadow-md`
                              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                          }`}
                        >
                          <div className="font-semibold text-sm">{framework.name}</div>
                          {selectedFramework === framework.id && (
                            <CheckCircle className="w-4 h-4 mt-1 mx-auto" />
                          )}
                        </button>
                      ))}
                    </div>
                    
                    {/* Display Prompt Version */}
                    {selectedFramework && promptVersion && (
                      <div className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 flex items-center justify-between">
                        <span className="font-medium">System Prompt Version:</span>
                        <Badge variant="outline" className="bg-white">v{promptVersion}</Badge>
                      </div>
                    )}
                  </div>

                  {/* Language Selection */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">
                      Select Assessment Language
                    </h3>
                    <select
                      value={selectedLanguage}
                      onChange={(e) => setSelectedLanguage(e.target.value)}
                      data-testid="language-select"
                      className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500 bg-white text-slate-900"
                    >
                      {languages.map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.flag} {lang.name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-2">
                      The AI assistant will conduct the entire assessment in your selected language
                    </p>
                  </div>

                  {/* Info Cards */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <Card className="border-purple-200 bg-purple-50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Clock className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-semibold text-sm text-purple-900">~45 Minute Assessment</h4>
                            <p className="text-xs text-purple-700 mt-1">
                              Comprehensive coverage of all compliance domains
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-green-200 bg-green-50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <FileText className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-semibold text-sm text-green-900">Dynamic Questions</h4>
                            <p className="text-xs text-green-700 mt-1">
                              AI-generated questions tailored to your framework
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* No API Key Warning */}
                  {!hasOpenAIKey && (
                    <Card className="border-red-200 bg-red-50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-semibold text-sm text-red-900">Configuration Required</h4>
                            <p className="text-xs text-red-800 mt-1">
                              OpenAI API key is not configured. Please contact your administrator.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Next Button */}
                  <Button
                    onClick={proceedToQuestionGeneration}
                    disabled={!selectedFramework || !hasOpenAIKey || generationProgress.isGenerating}
                    data-testid="proceed-to-questions-btn"
                    className="w-full bg-navy-600 hover:bg-navy-700 text-white py-6 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generationProgress.isGenerating ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Loading Assessment Plan...
                      </>
                    ) : (
                      <>
                        Continue to Question Generation
                        <ArrowLeft className="w-5 h-5 ml-2 rotate-180" />
                      </>
                    )}
                  </Button>

                  {!selectedFramework && (
                    <p className="text-sm text-amber-600 text-center">
                      Please select a framework to continue
                    </p>
                  )}
                </>
              )}

              {/* STEP 2: Question Generation */}
              {currentStep === 2 && (
                <>
                  {/* Assessment Plan Summary */}
                  {assessmentPlan && (
                    <Card className="border-navy-200 bg-gradient-to-r from-navy-50 to-blue-50">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-semibold text-navy-900">Assessment Plan</h3>
                          {assessmentPlan.prompts_used && assessmentPlan.prompts_used[0] && (
                            <Badge variant="outline" className="bg-white">
                              {assessmentPlan.prompts_used[0].reference}
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                          <div>
                            <p className="text-2xl font-bold text-navy-600">{assessmentPlan.total_categories}</p>
                            <p className="text-xs text-slate-600">Categories</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-navy-600">{assessmentPlan.questions_per_category}</p>
                            <p className="text-xs text-slate-600">Questions/Category</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-navy-600">{assessmentPlan.total_estimated_questions}</p>
                            <p className="text-xs text-slate-600">Total Questions</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-green-600">~{assessmentPlan.estimated_duration_minutes}m</p>
                            <p className="text-xs text-slate-600">Est. Duration</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Generation Progress */}
                  {generationProgress.isGenerating && (
                    <Card className="border-blue-200 bg-blue-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                          <span className="font-semibold text-blue-900">
                            Generating Questions...
                          </span>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="mb-3">
                          <div className="flex justify-between text-xs text-slate-600 mb-1">
                            <span>Category {generationProgress.currentCategoryIndex + 1} of {generationProgress.totalCategories}</span>
                            <span>{Math.round(((generationProgress.currentCategoryIndex) / generationProgress.totalCategories) * 100)}%</span>
                          </div>
                          <div className="w-full bg-slate-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${(generationProgress.currentCategoryIndex / generationProgress.totalCategories) * 100}%` }}
                            />
                          </div>
                        </div>
                        
                        <p className="text-sm text-blue-800">
                          Currently generating: <span className="font-medium">{generationProgress.currentCategory}</span>
                        </p>
                        <p className="text-xs text-slate-600 mt-1">
                          {generationProgress.totalQuestionsGenerated} questions generated so far
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Categories List */}
                  {assessmentPlan && assessmentPlan.categories && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-3">
                        Categories to Cover ({assessmentPlan.categories.length})
                      </h3>
                      <div className="grid gap-2 max-h-64 overflow-y-auto pr-2">
                        {assessmentPlan.categories.map((cat, idx) => (
                          <div 
                            key={idx}
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              cat.status === 'completed' 
                                ? 'bg-green-50 border-green-200' 
                                : cat.status === 'failed'
                                ? 'bg-red-50 border-red-200'
                                : generationProgress.currentCategory === cat.name
                                ? 'bg-blue-50 border-blue-300'
                                : 'bg-white border-slate-200'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {cat.status === 'completed' ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : cat.status === 'failed' ? (
                                <AlertCircle className="w-4 h-4 text-red-600" />
                              ) : generationProgress.currentCategory === cat.name ? (
                                <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                              ) : (
                                <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                              )}
                              <span className="text-sm font-medium text-slate-800">{cat.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {cat.framework}
                              </Badge>
                              {cat.questions && cat.questions.length > 0 && (
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  {cat.questions.length} Q
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Generation Results Summary */}
                  {generatedQuestionnaire && !generationProgress.isGenerating && (
                    <Card className="border-green-200 bg-green-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="font-semibold text-green-900">Questions Generated Successfully</span>
                        </div>
                        <p className="text-sm text-green-800">
                          {generatedQuestionnaire.total_questions} questions ready across {generationProgress.completedCategories.length} categories
                        </p>
                        {generationProgress.failedCategories.length > 0 && (
                          <p className="text-xs text-amber-700 mt-1">
                            {generationProgress.failedCategories.length} categories had issues
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => goBackToStep(1)}
                      disabled={generationProgress.isGenerating}
                      className="flex-1"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    
                    {!generatedQuestionnaire ? (
                      <Button
                        onClick={startQuestionGeneration}
                        disabled={generationProgress.isGenerating || !assessmentPlan}
                        data-testid="generate-questions-btn"
                        className="flex-1 bg-navy-600 hover:bg-navy-700 text-white"
                      >
                        {generationProgress.isGenerating ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            Generate Questions
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button
                        onClick={proceedToVoiceAssessment}
                        data-testid="start-voice-assessment-btn"
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      >
                        Start Voice Assessment
                        <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                      </Button>
                    )}
                  </div>
                </>
              )}

              {/* STEP 3: Ready to Start Voice Assessment */}
              {currentStep === 3 && (
                <>
                  {/* Questionnaire Summary */}
                  {generatedQuestionnaire && (
                    <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="font-semibold text-green-900">Questionnaire Ready</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-2xl font-bold text-green-600">{generatedQuestionnaire.total_questions}</p>
                            <p className="text-xs text-slate-600">Questions</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-green-600">{generationProgress.completedCategories.length}</p>
                            <p className="text-xs text-slate-600">Categories</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-green-600">~45m</p>
                            <p className="text-xs text-slate-600">Duration</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Assessment Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <Card className="border-blue-200 bg-blue-50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Mic className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-semibold text-sm text-blue-900">Voice-Powered</h4>
                            <p className="text-xs text-blue-700 mt-1">
                              Speak naturally with Nova, your AI compliance assistant
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-purple-200 bg-purple-50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <FileText className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="font-semibold text-sm text-purple-900">Full Report</h4>
                            <p className="text-xs text-purple-700 mt-1">
                              Receive a detailed compliance assessment report
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Selected Framework & Language Display */}
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <p className="text-xs text-slate-600 mb-1">Framework</p>
                      <Badge className={frameworks.find(f => f.id === selectedFramework)?.color || 'bg-slate-100'}>
                        {frameworks.find(f => f.id === selectedFramework)?.name || selectedFramework}
                      </Badge>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-slate-600 mb-1">Language</p>
                      <Badge className="bg-purple-100 text-purple-700">
                        {languages.find(l => l.code === selectedLanguage)?.flag} {languages.find(l => l.code === selectedLanguage)?.name}
                      </Badge>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => goBackToStep(2)}
                      className="flex-1"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    
                    <Button
                      onClick={startAssessment}
                      disabled={!hasOpenAIKey || isStarting}
                      data-testid="start-assessment-btn"
                      className="flex-1 bg-navy-600 hover:bg-navy-700 text-white py-6 text-lg font-semibold"
                    >
                      <Mic className="w-5 h-5 mr-2" />
                      {isStarting ? 'Starting...' : 'Start Voice Assessment'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          // Active Assessment Screen
          <Card className="shadow-xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl flex items-center gap-2">
                  <div className="relative">
                    <Mic className={`w-6 h-6 ${isListening ? 'text-green-600' : 'text-slate-400'}`} />
                    {isListening && (
                      <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                    )}
                  </div>
                  Assessment in Progress
                </CardTitle>
                <Badge className={isPaused ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                  {isPaused ? 'Paused' : 'Active'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Timer */}
              <Card className="border-navy-200 bg-navy-50">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 mb-1">Time Elapsed</p>
                      <p className="text-3xl font-bold text-navy-900">{formatTime(elapsedTime)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-600 mb-1">Time Remaining</p>
                      <p className="text-3xl font-bold text-navy-900">{formatTime(getRemainingTime())}</p>
                    </div>
                  </div>
                  <div className="mt-4 w-full bg-slate-200 rounded-full h-2">
                    <div 
                      className="bg-navy-600 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${(elapsedTime / MAX_DURATION) * 100}%` }}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Selected Framework Display */}
              <Card className="border-2 bg-gradient-to-r from-blue-50 to-indigo-50" style={{ borderColor: '#1e3a8a' }}>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#1e3a8a' }}>Assessment Framework</p>
                    <div className="flex items-center justify-between">
                      <Badge 
                        className="text-sm py-1 px-3 font-medium"
                        style={{ 
                          backgroundColor: '#1e3a8a',
                          color: 'white',
                          border: 'none'
                        }}
                      >
                        {frameworks.find(f => f.id === selectedFramework)?.name || selectedFramework}
                      </Badge>
                      {promptVersion && (
                        <span className="text-xs text-slate-600">
                          Prompt v{promptVersion}
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* AI Status Indicator */}
              <Card className="border-2 border-navy-300 bg-gradient-to-r from-navy-50 to-blue-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${getStatusInfo().color.replace('text-', 'bg-')} ${getStatusInfo().icon}`} />
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Nova Status</p>
                        <p className={`text-sm ${getStatusInfo().color}`}>{getStatusInfo().text}</p>
                      </div>
                    </div>
                    <Mic className={`w-6 h-6 ${isListening ? 'text-green-600' : 'text-slate-400'}`} />
                  </div>
                </CardContent>
              </Card>

              {/* Tabs for Conversation vs Info */}
              <div className="flex gap-2 border-b border-slate-200">
                <button
                  onClick={() => setActiveTab('conversation')}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeTab === 'conversation'
                      ? 'text-navy-600 border-b-2 border-navy-600'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Live Transcript
                </button>
                <button
                  onClick={() => setActiveTab('info')}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeTab === 'info'
                      ? 'text-navy-600 border-b-2 border-navy-600'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Session Info
                </button>
              </div>

              {/* Tab Content */}
              {activeTab === 'conversation' ? (
                <Card className="border-slate-200">
                  <CardContent className="p-4">
                    <div className="bg-slate-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                      {transcript.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-8">
                          Conversation will appear here as you speak with Nova...
                        </p>
                      ) : (
                        <div className="space-y-4">
                          {transcript.map((turn, idx) => (
                            <div
                              key={idx}
                              className={`flex ${turn.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-[80%] rounded-lg px-4 py-2 ${
                                  turn.role === 'user'
                                    ? 'bg-navy-600 text-white'
                                    : 'bg-white text-slate-900 border border-slate-200'
                                }`}
                              >
                                <p className="text-xs font-semibold mb-1 opacity-75">
                                  {turn.role === 'user' ? 'You' : 'Nova'}
                                </p>
                                <p className="text-sm whitespace-pre-wrap">{turn.content}</p>
                              </div>
                            </div>
                          ))}
                          <div ref={transcriptEndRef} />
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-slate-600 mb-2">Selected Framework:</p>
                    <div className="flex items-center gap-3">
                      <Badge className={frameworks.find(f => f.id === selectedFramework)?.color}>
                        {frameworks.find(f => f.id === selectedFramework)?.name || selectedFramework}
                      </Badge>
                      {promptVersion && (
                        <span className="text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">
                          Prompt v{promptVersion}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-slate-600 mb-2">Assessment Language:</p>
                    <Badge className="bg-purple-100 text-purple-700">
                      {languages.find(l => l.code === selectedLanguage)?.flag} {languages.find(l => l.code === selectedLanguage)?.name}
                    </Badge>
                  </div>
                </div>
              )}

              {/* Controls */}
              <div className="flex justify-center">
                <Button
                  onClick={handleEndAssessment}
                  variant="destructive"
                  className="px-8 py-6"
                  size="lg"
                >
                  <StopCircle className="w-5 h-5 mr-2" />
                  End Assessment
                </Button>
              </div>

              {/* Warning for time */}
              {getRemainingTime() < 300 && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                      <p className="text-sm text-amber-800">
                        Less than 5 minutes remaining
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default VoiceAssessment;
